/**
 * How a transaction reads on screen. Shared so the customer's own Transactions page and the staff-side
 * customer detail page label the same record identically — support and the customer should never be
 * looking at different words for the same state.
 */

const TYPE_LABEL: Record<string, string> = {
  atf4: 'ATF', swft: 'SWFT', doce: 'DOCE', 'conv-atf4': 'ATF conversion', 'conv-swft': 'SWFT conversion',
};

const STATE_TONE: Record<string, string> = {
  // ACK = the FBI acknowledged a DOCE submission; ERROR = the FBI's response carried an error, as
  // distinct from REJECTED, which is our own pipeline rejecting the work.
  COMPLETE: 'ok', SUBMITTED: 'ship', ACK: 'ship', PENDING_PAYMENT: 'pend', DRAFT: 'pend',
  // A card conversion the auto-segmenter could not do — with a person now, not stalled.
  IN_REVIEW: 'pend',
  ERROR: 'bad', REJECTED: 'bad',
};

/** Falls back to the raw value: a new capture type should still show, not vanish. */
export const typeLabel = (captureType: string) => TYPE_LABEL[captureType] ?? captureType;

export const stateTone = (state: string) => STATE_TONE[state] ?? 'pend';

export const fmtDate = (iso?: string | null) => (iso ? new Date(iso).toLocaleDateString() : '—');

/**
 * When a transaction was created, in the reader's OWN timezone.
 *
 * `createdOn` is stored as UTC ISO, and the date alone was ambiguous in a way that matters here: a capture
 * taken at 8pm Eastern is stored as the following day in UTC, so an operator comparing our list against their
 * own records saw a date that disagreed with the day they remembered working. Rendering the instant with
 * `toLocaleString` resolves it to whatever zone the browser is in, which is the one the person reading it
 * is standing in.
 *
 * Deliberately NOT stamped with a timezone name. Everyone reading a given screen — customer or the support
 * agent looking at the same record — sees their own local time, and labelling it "EDT" for a reader in
 * Arizona would be worse than leaving it implicit.
 *
 * Invalid input renders as an em dash rather than "Invalid Date". A record with a malformed timestamp is
 * still a record worth showing.
 */
export const fmtDateTime = (iso?: string | null) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
};

/**
 * States a customer may delete from — the SAME set the API enforces (`DELETABLE_STATES` in
 * `services/lib/domain/transactions.ts`).
 *
 * Duplicated deliberately rather than fetched: the server is the authority and refuses regardless, but a
 * button that is offered and then rejected is worse than one that was never offered. If the two ever
 * disagree the server wins and the customer sees a clear refusal, which is the safe direction.
 */
const DELETABLE = new Set(['DRAFT', 'COMPLETE', 'ERROR', 'REJECTED']);

export const canDelete = (state: string) => DELETABLE.has(state);

/** Why the button is disabled, for its tooltip. Null when it is not. */
export function deleteBlockedReason(state: string): string | null {
  if (canDelete(state)) return null;
  if (state === 'IN_REVIEW') return 'Being processed by hand — can be deleted once it finishes';
  return `Still ${state.toLowerCase().replace(/_/g, ' ')} — can be deleted once it finishes`;
}
